/*
 * EnviAir — Compare page
 *
 * Liquid cannot read a query string, so the product list is resolved here:
 *   ?products=handle1,handle2   (the format the compare bar sends)
 *
 * Each handle is fetched through the Section Rendering API, which returns the
 * compare-data section — JSON carrying the fields only Liquid can produce
 * (metafield specifications, money formatting, truncated description).
 *
 * With no products in the URL the picker takes over, backed by Shopify's
 * predictive search endpoint so the catalogue is reachable without embedding
 * it in the page.
 */
(function () {
  var root = document.querySelector('[data-ea-compare-page]');
  if (!root) return;

  var MAX = parseInt(root.dataset.max, 10) || 4;
  var LABELS = JSON.parse(root.dataset.labels || '{}');

  var picker = root.querySelector('[data-ea-compare-picker]');
  var search = root.querySelector('[data-ea-compare-search]');
  var results = root.querySelector('[data-ea-compare-results]');
  var chosenList = root.querySelector('[data-ea-compare-chosen]');
  var goButton = root.querySelector('[data-ea-compare-go]');
  var scroll = root.querySelector('[data-ea-compare-scroll]');
  var table = root.querySelector('[data-ea-compare-table]');
  var status = root.querySelector('[data-ea-compare-status]');

  var chosen = [];

  // --- helpers ------------------------------------------------------------

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function handlesFromUrl() {
    var raw = new URLSearchParams(window.location.search).get('products');
    if (!raw) return [];
    return raw
      .split(',')
      .map(function (h) {
        return h.trim();
      })
      .filter(Boolean)
      .slice(0, MAX);
  }

  function say(message) {
    status.textContent = message || '';
    status.hidden = !message;
  }

  // --- data ---------------------------------------------------------------

  function fetchProduct(handle) {
    return fetch('/products/' + encodeURIComponent(handle) + '?section_id=enviair-compare-data')
      .then(function (response) {
        if (!response.ok) throw new Error(handle);
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var node = doc.querySelector('[data-ea-compare-data]');
        if (!node) throw new Error(handle);
        return JSON.parse(node.textContent);
      });
  }

  // --- table --------------------------------------------------------------

  function buildTable(products) {
    table.innerHTML = '';

    // Specification keys differ per product, so the rows are the union of
    // every heading, in first-seen order. A product missing one gets a dash
    // rather than a collapsed row, keeping the columns aligned.
    var specKeys = [];
    products.forEach(function (p) {
      (p.specs || []).forEach(function (s) {
        if (s.heading && specKeys.indexOf(s.heading) === -1) specKeys.push(s.heading);
      });
    });

    table.style.setProperty('--ea-cmp-columns', products.length);

    var rows = [
      { type: 'header' },
      { type: 'price', label: LABELS.price },
      { type: 'availability', label: LABELS.availability },
      { type: 'cart', label: '' },
      { type: 'description', label: LABELS.description },
    ];

    if (specKeys.length) {
      rows.push({ type: 'specHeading', label: LABELS.specifications });
      specKeys.forEach(function (key) {
        rows.push({ type: 'spec', label: key });
      });
    }

    rows.forEach(function (row) {
      var rowEl = el('div', 'ea-cmp__row ea-cmp__row--' + row.type);

      var label = el('div', 'ea-cmp__label', row.label || '');
      rowEl.appendChild(label);

      products.forEach(function (p) {
        var cell = el('div', 'ea-cmp__cell');

        if (row.type === 'header') {
          cell.appendChild(buildHeader(p));
        } else if (row.type === 'price') {
          var price = el('div', 'ea-cmp__price');
          price.appendChild(el('span', 'ea-cmp__price-now', p.price));
          if (p.onSale > 0 && p.compareAt) {
            price.appendChild(el('s', 'ea-cmp__price-was', p.compareAt));
          }
          cell.appendChild(price);
        } else if (row.type === 'availability') {
          var stock = el(
            'span',
            'ea-cmp__stock ' + (p.available ? 'ea-cmp__stock--in' : 'ea-cmp__stock--out'),
            p.available ? LABELS.inStock : LABELS.outOfStock
          );
          cell.appendChild(stock);
        } else if (row.type === 'cart') {
          cell.appendChild(buildCart(p));
        } else if (row.type === 'description') {
          cell.appendChild(el('p', 'ea-cmp__desc', p.description || '—'));
        } else if (row.type === 'spec') {
          var match = (p.specs || []).filter(function (s) {
            return s.heading === row.label;
          })[0];
          cell.textContent = (match && match.value) || '—';
        }

        rowEl.appendChild(cell);
      });

      table.appendChild(rowEl);
    });

    scroll.hidden = false;
    picker.hidden = true;
  }

  function buildHeader(p) {
    var wrap = el('div', 'ea-cmp__product');

    var link = el('a', 'ea-cmp__product-link');
    link.href = p.url;

    if (p.image) {
      var img = new Image();
      img.className = 'ea-cmp__img';
      img.src = p.image;
      img.alt = p.imageAlt || '';
      img.loading = 'lazy';
      link.appendChild(img);
    }

    link.appendChild(el('span', 'ea-cmp__product-title', p.title));
    wrap.appendChild(link);

    var remove = el('button', 'ea-cmp__remove', LABELS.remove);
    remove.type = 'button';
    remove.addEventListener('click', function () {
      var next = handlesFromUrl().filter(function (h) {
        return h !== p.handle;
      });
      setUrl(next);
      render();
    });
    wrap.appendChild(remove);

    return wrap;
  }

  function buildCart(p) {
    if (!p.available) {
      var out = el('span', 'ea-cmp__cart ea-cmp__cart--disabled', LABELS.outOfStock);
      return out;
    }

    // Multiple variants need a choice, so send them to the product page rather
    // than silently adding the first one.
    if (!p.singleVariant) {
      var choose = el('a', 'ea-cmp__cart ea-cmp__cart--link', LABELS.chooseOptions);
      choose.href = p.url;
      return choose;
    }

    var button = el('button', 'ea-cmp__cart', LABELS.addToCart);
    button.type = 'button';
    button.addEventListener('click', function () {
      button.disabled = true;
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: p.variantId, quantity: 1 }] }),
      })
        .then(function (response) {
          if (!response.ok) throw new Error('add');
          button.textContent = LABELS.added;
          document.dispatchEvent(new CustomEvent('ea:cart:updated'));
          setTimeout(function () {
            button.textContent = LABELS.addToCart;
            button.disabled = false;
          }, 2000);
        })
        .catch(function () {
          // Never leave a dead button — fall back to the product page.
          window.location.href = p.url;
        });
    });
    return button;
  }

  // --- picker -------------------------------------------------------------

  function renderChosen() {
    chosenList.innerHTML = '';
    chosen.forEach(function (p) {
      var item = el('li', 'ea-cmp__chip');
      item.appendChild(el('span', null, p.title));

      var remove = el('button', 'ea-cmp__chip-remove');
      remove.type = 'button';
      remove.setAttribute('aria-label', LABELS.remove + ' ' + p.title);
      remove.textContent = '×';
      remove.addEventListener('click', function () {
        chosen = chosen.filter(function (c) {
          return c.handle !== p.handle;
        });
        renderChosen();
      });
      item.appendChild(remove);
      chosenList.appendChild(item);
    });

    goButton.disabled = chosen.length < 2;
  }

  function closeResults() {
    results.hidden = true;
    results.innerHTML = '';
    search.setAttribute('aria-expanded', 'false');
  }

  function runSearch(term) {
    if (term.length < 2) return closeResults();

    var url =
      '/search/suggest.json?q=' +
      encodeURIComponent(term) +
      '&resources[type]=product&resources[limit]=6';

    fetch(url)
      .then(function (r) {
        return r.json();
      })
      .then(function (data) {
        var products = (data.resources && data.resources.results.products) || [];
        results.innerHTML = '';

        if (!products.length) {
          results.appendChild(el('li', 'ea-cmp__result ea-cmp__result--empty', 'No matches'));
        }

        products.forEach(function (p) {
          var item = el('li');
          var button = el('button', 'ea-cmp__result', p.title);
          button.type = 'button';
          button.setAttribute('role', 'option');
          button.addEventListener('click', function () {
            var already = chosen.some(function (c) {
              return c.handle === p.handle;
            });
            if (!already && chosen.length < MAX) chosen.push({ handle: p.handle, title: p.title });
            search.value = '';
            closeResults();
            renderChosen();
          });
          item.appendChild(button);
          results.appendChild(item);
        });

        results.hidden = false;
        search.setAttribute('aria-expanded', 'true');
      })
      .catch(closeResults);
  }

  var searchTimer;
  search.addEventListener('input', function () {
    clearTimeout(searchTimer);
    var term = search.value.trim();
    searchTimer = setTimeout(function () {
      runSearch(term);
    }, 220);
  });

  search.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') closeResults();
  });

  document.addEventListener('click', function (event) {
    if (!picker.contains(event.target)) closeResults();
  });

  goButton.addEventListener('click', function () {
    setUrl(
      chosen.map(function (c) {
        return c.handle;
      })
    );
    render();
  });

  function setUrl(handles) {
    var url = new URL(window.location.href);
    if (handles.length) {
      url.searchParams.set('products', handles.join(','));
    } else {
      url.searchParams.delete('products');
    }
    window.history.replaceState({}, '', url);
  }

  function showPicker() {
    scroll.hidden = true;
    picker.hidden = false;
    renderChosen();
  }

  // --- boot ---------------------------------------------------------------

  function render() {
    var handles = handlesFromUrl();

    if (handles.length < 1) {
      say('');
      showPicker();
      return;
    }

    say('Loading…');

    Promise.all(
      handles.map(function (h) {
        return fetchProduct(h).catch(function () {
          return null;
        });
      })
    ).then(function (products) {
      var found = products.filter(Boolean);

      if (!found.length) {
        say('');
        showPicker();
        return;
      }

      say('');
      chosen = found.map(function (p) {
        return { handle: p.handle, title: p.title };
      });
      buildTable(found);
    });
  }

  render();
})();
